import { makeApolloClient, logError, emitWithAck } from '../util';
import { CHECKLISTS, CHECKLIST_COUNTS } from 'queries/checklists';
import { tryParse } from 'utils';
import {
	FETCH_CHECKLISTS,
	FETCH_CHECKLISTS_FAILED,
	CLEAR_CHECKLISTS,
	UPSERT_CHECKLIST,
	REMOVE_CHECKLIST,
} from './actionTypes';

const CHECKLIST_ACK_TIMEOUT_MS = 20000;

export const setChecklists = checklists => ({ type: FETCH_CHECKLISTS, checklists });
export const upsertChecklist = checklist => ({ type: UPSERT_CHECKLIST, checklist });
export const removeChecklist = checklistID => ({ type: REMOVE_CHECKLIST, checklistID });
export const clearChecklists = () => ({ type: CLEAR_CHECKLISTS });

// The backend's errorJSON puts the user-facing message in JSON inside error.message.
export const checklistErrorMessage = error => {
	const parsed = tryParse(error?.message);
	return parsed?.message || error?.message || 'Something went wrong';
};

const userFacingError = error => {
	if (error.disconnected) return "You're offline. Reconnect and try again.";
	if (error.timedOut)
		return 'The server did not confirm the change. Check your connection and try again.';
	return checklistErrorMessage(error);
};

const emitChecklist = async (getState, route, payload) => {
	const { ws, auth } = getState();
	try {
		return await emitWithAck(ws.socket, CHECKLIST_ACK_TIMEOUT_MS, route, payload, auth.auth0Token);
	} catch (error) {
		throw new Error(userFacingError(error));
	}
};

export const thunkFetchChecklists = eventID => async (dispatch, getState) => {
	const client = makeApolloClient(getState());
	try {
		const { checklists } = await client.request(CHECKLISTS, { event_id: eventID });
		dispatch(setChecklists(checklists));
	} catch (error) {
		logError(error, 'checklists.action.thunkFetchChecklists');
		dispatch({ type: FETCH_CHECKLISTS_FAILED });
	}
};

export const thunkFetchChecklistCounts = checklistID => async (dispatch, getState) => {
	const client = makeApolloClient(getState());
	try {
		const { checklist } = await client.request(CHECKLIST_COUNTS, { id: checklistID });
		return Object.fromEntries(checklist.completion_counts.map(c => [c.item_id, c.count]));
	} catch (error) {
		logError(error, 'checklists.action.thunkFetchChecklistCounts');
		// Unknown counts still make deletes confirm.
		return null;
	}
};

export const thunkSaveChecklist = ({ id, version, name, content }) => async (
	dispatch,
	getState
) => {
	const body = JSON.stringify(content);
	const data = id
		? await emitChecklist(getState, 'checklists/:checklistID/PATCH', {
				id,
				version,
				name,
				content: body,
		  })
		: await emitChecklist(getState, 'checklists/POST', {
				event_id: getState().events.currentEvent.id,
				name,
				content: body,
		  });
	dispatch(upsertChecklist(data));
	return data;
};

export const thunkReloadChecklist = checklistID => async (dispatch, getState) => {
	await dispatch(thunkFetchChecklists(getState().events.currentEvent.id));
	if (getState().checklists.loadFailed)
		throw new Error("Couldn't reload the checklist. Check your connection and try again.");
	return getState().checklists.checklists.find(checklist => checklist.id === checklistID);
};

export const thunkDeleteChecklist = checklistID => async (dispatch, getState) => {
	await emitChecklist(getState, 'checklists/:checklistID/DELETE', checklistID);
	dispatch(removeChecklist(checklistID));
};
